import React from 'react'
import { useTheme } from 'styled-components'

import { WebRendererCanvas } from '../../../../components/WebRendererCanvas'
import { PageCanvasSupportGate } from '../../../shares/PageCanvasView/components/PageCanvasSupportGate'
import { EntityOverlay, OriginContent } from '../../../canvas/components'

import PublicationCanvasControls from '../PublicationCanvasControls'

import { CanvasContainer } from './PublicationCanvas.styles'

import { PublicationPageFragment } from '@sketch/gql-types'
import {
  CanvasRendererProvider,
  usePRFileArtboards,
  useRootNodeAbsoluteExtent,
  PRMarinaLayerType,
  Rect,
  PRMarinaFileArtboard,
  convertEmbindVectorToArray,
} from '@sketch-hq/sketch-web-renderer'

interface PublicationCanvasFrameOverlayProps {
  frame: PRMarinaFileArtboard
  rootExtent: Rect
}

const PublicationCanvasFrameOverlay = React.memo(
  function PublicationCanvasFrameOverlay({
    frame,
    rootExtent,
  }: PublicationCanvasFrameOverlayProps) {
    const node = frame.getNode()

    return (
      <EntityOverlay
        bounds={frame.bounds}
        artboardName={frame.name}
        artboardUUID={frame.objectId}
        isComponent={
          node?.getLayer()?.type.value === PRMarinaLayerType.SymbolMaster
        }
        variantProperties={
          node
            ? convertEmbindVectorToArray(node.getVariantConfiguration()).map(
                ({ propertyName, valueName }) => ({
                  propertyName,
                  valueName,
                })
              )
            : undefined
        }
        disableAutoHover={true}
        rootExtent={rootExtent}
      />
    )
  }
)

const PublicationCanvasFramesOverlays = React.memo(
  function PublicationCanvasFramesOverlays() {
    const topLevelFrames = usePRFileArtboards() ?? []
    const rootExtent = useRootNodeAbsoluteExtent()

    return (
      <>
        {topLevelFrames.map(frame => (
          <PublicationCanvasFrameOverlay
            key={frame.objectId}
            frame={frame}
            rootExtent={rootExtent}
          />
        ))}
      </>
    )
  }
)

interface PublicationCanvasProps {
  shareIdentifier: string
  versionShortId: string
  page: PublicationPageFragment
  onPageSelected: (page: PublicationPageFragment) => void
}

const PublicationCanvas: React.FCC<PublicationCanvasProps> = ({
  shareIdentifier,
  versionShortId,
  page,
  onPageSelected,
}) => {
  const theme = useTheme()

  if (!page.uuid) return null

  return (
    <CanvasRendererProvider>
      <PageCanvasSupportGate>
        <CanvasContainer>
          <WebRendererCanvas
            pageUUID={page.uuid}
            page={page}
            versionShortId={versionShortId}
            shareIdentifier={shareIdentifier}
            backgroundColor={theme.colors.background.canvas.A}
          >
            <OriginContent>
              <PublicationCanvasFramesOverlays />
            </OriginContent>
          </WebRendererCanvas>
          <PublicationCanvasControls
            identifier={shareIdentifier}
            page={page}
            onPageSelected={onPageSelected}
          />
        </CanvasContainer>
      </PageCanvasSupportGate>
    </CanvasRendererProvider>
  )
}

export default PublicationCanvas
